import React, { useState, useEffect, useRef } from "react";
import { useSnackBar } from "../../../hooks/useSnackBar";
import "./Drivers.css";

// components imports
import DriversTable from "../../../Components/BoltTable/BoltTable";
import SwitchButton from "../../../Components/SwitchButton/SwitchButton";
import Confirm from "../../../Components/Popups/Confirm";
import Tooltip from "../../../Components/Tooltip/Tooltip";
import DriverForm from "./Components/DriverForm/DriverForm";
import BoltButton from "../../../Components/UI/Buttons/BoltButton";
// import UpdateDriver from "./Components/UpdateDriver/UpdateDriver";

// apis imports
import {
  deleteDriver,
  fetchAllDrivers,
  changeStatus,
} from "../../../services/drivers.services";

// library imports
import noImage from "../../../assets/images/noImage.png";
import { FaRegEdit } from "react-icons/fa";
import { BsTrash } from "react-icons/bs";
import { FcPlus } from "react-icons/fc";
import { useTranslation } from "react-i18next";

const Drivers = () => {
  const { t } = useTranslation("carrier");
  const [Change, setChange] = useState();
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);

  const [drivers, setDrivers] = useState([]);

  const [manageDriverModal, setManageDriverModal] = useState(false);
  const [deleteDriverModal, setDeleteDriverModal] = useState(false);

  const setSnack = useSnackBar();

  const columns = [
    {
      id: 0,
      label: "#",
      render: (row) => <>{row?.id ?? "-"}</>,
      width: "200px",
    },
    {
      id: 1,
      label: t("drivers.active"),
      render: (row) => (
        <>
          <SwitchButton
            onChange={(e) => handleStatusChange(e, row)}
            checked={row?.driver?.active === "true"}
            disabled={Change ? true : false}
          />
        </>
      ),
      width: "200px",
    },
    {
      id: 2,
      label: t("drivers.profilePicture"),
      render: (row) => (
        <>
          <img
            src={row?.driver?.picture ?? noImage}
            onError={(e) => {
              e.target.src = noImage;
            }}
            loading="lazy"
          />
        </>
      ),
      width: "200px",
    },
    {
      id: 3,
      label: t("drivers.username"),
      render: (row) => <>{row?.username ?? "-"}</>,
      width: "200px",
    },
    {
      id: 4,
      label: t("drivers.name"),
      render: (row) => <>{row?.driver?.name ?? "-"}</>,
      width: "200px",
    },
    {
      id: 5,
      label: t("drivers.phoneNumber"),
      render: (row) => <>{row?.driver?.phone ?? "-"}</>,
      width: "200px",
    },
    {
      id: 6,
      label: t("drivers.email"),
      render: (row) => <>{row?.email ?? "-"}</>,
      width: "200px",
    },
    {
      id: 7,
      label: t("drivers.vehicle"),
      render: (row) => (
        <>
          {(() => {
            if (row?.driver?.vehicle) {
              const { license_plate, model, type } = row?.driver?.vehicle;
              const vehicleString = `${license_plate}, ${model}, ${type}`;
              return vehicleString;
            } else {
              return t("drivers.noVehicleSelected");
            }
          })()}
        </>
      ),
      width: "200px",
    },
    {
      id: 8,
      label: t("drivers.actions"),
      render: (row) => (
        <div className="table_action__wrap">
          <Tooltip title={t("drivers.editDriver")}>
            <div
              className="table_action__btn"
              onClick={() => setManageDriverModal(row)}
            >
              <FaRegEdit size={7} />
              {t("common:actions.edit")}
            </div>
          </Tooltip>
          <Tooltip title={t("drivers.deleteDriver")}>
            <div
              className="table_action__btn"
              onClick={() => setDeleteDriverModal(row.id)}
            >
              <BsTrash size={7} />
              {t("common:actions.delete")}
            </div>
          </Tooltip>
        </div>
      ),
      width: "200px",
      fixed: "right",
    },
  ];

  const deleteSingleDriver = async () => {
    try {
      setLoading(true);
      const deleted = await deleteDriver(deleteDriverModal);
      if (deleted.error) {
        setSnack((snack) => {
          return {
            ...snack,
            open: true,
            text: deleted?.error?.message || t("drivers.couldntDeleteDriver"),
            severity: "error",
          };
        });
        setLoading(false);
      } else {
        setDrivers((prevData) => {
          return {
            ...prevData,
            data: prevData?.data?.filter(
              (driver) => driver.id !== deleteDriverModal,
            ),
          };
        });
        setSnack((snack) => {
          return {
            ...snack,
            open: true,
            text: deleted?.data?.message,
            severity: "success",
          };
        });
        setLoading(false);
        setDeleteDriverModal(false);
      }
    } catch (error) {
      setSnack((snack) => {
        return {
          ...snack,
          open: true,
          text: error?.message || t("common:messages.somethingWentWrong"),
          severity: "error",
        };
      });
      setLoading(false);
    }
  };

  const getAllDrivers = async (page, perPage, filter) => {
    const response = await fetchAllDrivers(page, perPage, filter);
    if (!response.error) {
      setDrivers(response.data.result);
    } else {
      setSnack((snack) => {
        return {
          ...snack,
          open: true,
          text: t("common:messages.somethingWentWrong"),
          severity: "error",
        };
      });
    }
  };

  const handleStatusChange = async (e, record) => {
    try {
      setChange(true);
      setDrivers((prevData) => {
        return {
          ...prevData,
          data: prevData?.data?.map((item) =>
            item.id == record.id
              ? {
                  ...item,
                  driver: {
                    ...item.driver,
                    active: e.target.checked ? "true" : "false",
                  },
                }
              : item,
          ),
        };
      });
      const updated = await changeStatus({
        record_id: parseInt(record?.id),
        active: e.target.checked ? "true" : "false",
      });
      if (updated.error) {
        setSnack((snack) => {
          return {
            ...snack,
            open: true,
            text: updated.error.message,
            severity: "error",
          };
        });
      } else {
        setSnack((snack) => {
          return {
            ...snack,
            open: true,
            text: e.target.checked
              ? t("drivers.statusActive")
              : t("drivers.statusInactive"),
            severity: e.target.checked ? "success" : "info",
          };
        });
      }
      setChange(false);
    } catch (error) {
      setSnack((snack) => ({
        ...snack,
        open: true,
        text: error?.message || t("common:messages.somethingWentWrong"),
        severity: "error",
      }));
    }
  };

  return (
    <div
      className="dispatcher__screen"
      style={{ maxWidth: "100%", padding: "5px 5px" }}
    >
      <DriverForm
        setRefresh={setRefresh}
        open={manageDriverModal}
        setOpen={setManageDriverModal}
        setSnack={setSnack}
      />
      <Confirm
        open={deleteDriverModal}
        setOpen={setDeleteDriverModal}
        handleConfirm={deleteSingleDriver}
        text={t("drivers.doYouWantToDelete")}
        loader={loading}
      />
      <div className="custom__table_size1 actionbtn__needtobe_fixwidth">
        <DriversTable
          tableTitle={t("drivers.drivers")}
          tableId="driversTableForCarrier"
          message={t("drivers.noDriversAvailableToDisplay")}
          searchPlaceHolder={t("drivers.searchDrivers")}
          getAllData={getAllDrivers}
          data={drivers}
          columns={columns}
          refresh={refresh}
          headerButtons={
            <div className="flex gap-1.5 flex-wrap items-center">
              <BoltButton
                icon={FcPlus}
                label={t("drivers.addCaps")}
                onClick={() => setManageDriverModal(true)}
                variant="success"
                size="sm"
              />
            </div>
          }
        />
      </div>
    </div>
  );
};

export default Drivers;
